<template>
  <b-modal
    id="staging-modal"
    ok-only
    centered
    scrollable
    ok-title="Esci"
    title="Area di staging"
    size="xl"
    class="fixed-width"
    @show="onShow"
  >
    <MyLoading :loading-show="loadingShow" />
    <div
      v-if="ingesting"
      class="mx-md-3 mx-sm-1 mb-1"
    >
      <b-progress
        :value="100"
        :max="100"
        height="1.25rem"
        animated
        striped
      />
      <small class="d-block mt-50">{{ runningLabel }}</small>
    </div>
    <b-alert
      v-else-if="ingestStatus"
      :variant="ingestStatus.variant"
      class="mx-md-3 mx-sm-1"
      show
      dismissible
      @dismissed="ingestStatus = null"
    >
      <div class="alert-body">
        <strong>{{ ingestStatus.title }}</strong>
        <p
          v-for="line in ingestStatus.lines"
          :key="line"
          class="mb-0 mt-50"
        >
          {{ line }}
        </p>
        <ul
          v-if="ingestStatus.inProgress.length > 0"
          class="mb-0 mt-50"
        >
          <li
            v-for="path in ingestStatus.inProgress"
            :key="`progress-${path}`"
          >
            {{ path }}
          </li>
          <li v-if="ingestStatus.moreInProgress > 0">
            ...e altri {{ ingestStatus.moreInProgress }}
          </li>
        </ul>
        <ul
          v-if="ingestStatus.failed.length > 0"
          class="mb-0 mt-50"
        >
          <li
            v-for="entry in ingestStatus.failed"
            :key="`failed-${entry}`"
          >
            {{ entry }}
          </li>
          <li v-if="ingestStatus.moreFailed > 0">
            ...e altri {{ ingestStatus.moreFailed }}
          </li>
        </ul>
      </div>
    </b-alert>
    <b-alert
      variant="danger"
      class="mx-md-3 mx-sm-1"
      :show="Boolean(listError)"
    >
      <div class="alert-body">
        {{ listError }}
      </div>
    </b-alert>
    <div class="mx-md-3 mx-sm-1 py-1 justify-content-between">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        style="border: none"
        variant="outline-primary"
        @click="getStagingFiles"
      >
        <font-awesome-icon
          icon="arrows-rotate"
          size="lg"
        />
      </b-button>
      <b-form-group
        class="mb-0"
        label-align-sm="right"
        label-cols-sm="8"
        label-for="filter-input"
        label-size="sm"
      >
        <b-input-group>
          <b-form-input
            id="filter-input"
            v-model="filter"
            placeholder="Cerca"
            type="search"
          />

          <b-input-group-append>
            <b-button
              :disabled="!filter"
              class="btn-warning"
              @click="filter = ''"
            >
              Svouta
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
    </div>

    <vue-custom-scrollbar
      class="scroll-area"
      :settings="settings"
      style="height: 70vh"
    >

      <div class="my-card mx-md-3 mx-sm-1 text-center content-center" />
      <b-container
        class="d-flex justify-content-start flex-wrap"
        style="height: 500px"
      >
        <div
          v-for="file in filteredStagingFiles"
          :key="file.id"
          class="my-card mx-md-3 mx-sm-1 text-center content-center"
        >
          <FileCard
            :file="file"
            :staging="true"
            :selected-files="selectedStagingFiles"
            @select="selectFile"
          />
        </div>
      </b-container>
    </vue-custom-scrollbar>
    <RemoveModal
      id="stagingdelete-modal"
      :files="stagingFiles"
      :selected-files="selectedStagingFiles"
      @removed="getStagingFiles"
    />
    <MoveModal
      id="stagingmove-modal"
      :files="stagingFiles"
      :selected-files="selectedStagingFiles"
      @moved="getStagingFiles"
    />
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BContainer, BFormGroup, BInputGroup, BInputGroupAppend, BFormInput, BProgress, BAlert,
} from 'bootstrap-vue'
import { RepositoryFactory } from '@/api/RepositoryFactory'
import FileCard from '@/layouts/components/FileCard.vue'
import MyLoading from '@/layouts/components/MyLoading.vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import importFeedback from '@/helpers/import-feedback'
import apiErrors from '@/helpers/api-errors'
import vueCustomScrollbar from 'vue-custom-scrollbar'
import 'vue-custom-scrollbar/dist/vueScrollbar.css'
import RemoveModal from '@/layouts/components/modals/RemoveModal.vue'
import MoveModal from '@/layouts/components/modals/MoveModal.vue'

const objectsRepository = RepositoryFactory.get('objects')
const ftpRepository = RepositoryFactory.get('ftp')

const LIST_ERROR_MESSAGE = 'Impossibile caricare l\'elenco dei file in staging. Riprova con il pulsante di aggiornamento.'

export default {
  name: 'StagingModal',
  directives: {
    Ripple,
  },
  components: {
    vueCustomScrollbar,
    MyLoading,
    BModal,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BButton,
    BContainer,
    BProgress,
    BAlert,
    FileCard,
    RemoveModal,
    MoveModal,
  },
  data() {
    return {
      stagingFiles: [],
      loadingShow: false,
      settings: {
        suppressScrollX: true,
      },
      filter: null,
      ingesting: false,
      ingestStatus: null,
      ingestElapsed: 0,
      ingestTimer: null,
      listError: '',
    }
  },
  computed: {
    runningLabel() {
      return importFeedback.ingestRunningLabel(this.ingestElapsed)
    },
    selectedStagingFiles() {
      return this.stagingFiles.filter(file => file.selected === true)
    },
    filteredStagingFiles() {
      if (this.filter == null) {
        return this.stagingFiles
      }
      return this.stagingFiles.filter(file => file.label.toLowerCase().includes(this.filter.toLowerCase()))
    },
  },
  beforeDestroy() {
    this.stopTimer()
  },
  methods: {
    onShow() {
      this.ingestStatus = null
      this.listError = ''
      this.ingestAndGetStagingFiles()
    },
    stopTimer() {
      if (this.ingestTimer !== null) {
        window.clearInterval(this.ingestTimer)
        this.ingestTimer = null
      }
    },
    async getStagingFiles() {
      this.loadingShow = true
      this.listError = ''
      try {
        const { data } = await objectsRepository.indexStaging()

        if (data && data.code) {
          this.stagingFiles = data.data.objects
        } else {
          this.listError = LIST_ERROR_MESSAGE
        }
      } catch (error) {
        apiErrors.logApiError(error)
        this.listError = LIST_ERROR_MESSAGE
      } finally {
        this.loadingShow = false
      }
    },
    async ingestAndGetStagingFiles() {
      if (this.ingesting) {
        return
      }
      this.ingesting = true
      this.ingestStatus = null
      this.ingestElapsed = 0
      this.stopTimer()
      this.ingestTimer = window.setInterval(() => {
        this.ingestElapsed += 1
      }, 1000)

      // Niente overlay MyLoading durante l'ingest: coprirebbe lo stato in corso.
      try {
        // DS-8: timeout client limitato, altrimenti l'esito "timeout" non sarebbe raggiungibile
        const response = await ftpRepository.ingest({ timeout: importFeedback.INGEST_TIMEOUT_MS })
        this.ingestStatus = importFeedback.describeIngestReport(response && response.data)
      } catch (error) {
        apiErrors.logApiError(error)
        this.ingestStatus = importFeedback.classifyIngestFailure(error)
        if (this.ingestStatus.variant === 'danger') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Errore!',
              text: this.ingestStatus.title,
              icon: 'XIcon',
              variant: 'danger',
            },
          },
          {
            position: 'top-center',
          })
        }
      } finally {
        this.stopTimer()
        this.ingesting = false
      }

      // L'elenco va caricato sempre, anche se l'importazione è fallita
      await this.getStagingFiles()
    },
    selectFile(file) {
      this.$set(file, 'selected', !file.selected)
    },
  },
}
</script>

<style scoped>
.my-card {
  width: 13rem;
}
</style>
